import type { CSSProperties } from 'react';

import type { FieldDefinition, ValidationIssue } from '@inside-us-aos/domain';

import { DateInput } from '../DateInput';
import { TextInput } from '../TextInput';
import { SelectInput, type ISelectOption } from '../SelectInput';

import styles from './AddressComposite.module.css';

/**
 * The catalog stores postal addresses as several separate text /
 * select fields sharing a common prefix (e.g.,
 * `beneficiary.mailing_address.{street_number_and_name, unit_number,
 * city, state, zip_code, postal_code, province, ...}`). Phase 7a
 * Introduces the `AddressComposite` so the SectionRenderer
 * can collapse a detected address cluster into a single ARIA group
 * with consistent layout.
 *
 * The composite itself is a thin layout wrapper: each sub-field still
 * mounts the same primitive (TextInput / SelectInput) the
 * dispatcher would have mounted — just inside one fieldset/legend so
 * screen readers announce the address as a unit.
 *
 * The catalog does not declare the cluster's identity. The
 * SectionRenderer derives it from a heuristic on field-id prefixes
 * (anything ending in `.mailing_address.*`, `.safe_address.*`,
 * `.physical_address.*`, or `.address.*`). The detector lives in
 * SectionRenderer; this primitive is purely presentational.
 */
export interface IAddressFieldEntry {
  field: FieldDefinition;
  /**
   * Pre-computed value for the field. The caller (SectionRenderer)
   * reads from the engine and threads it here so this composite stays
   * agnostic of the engine.
   */
  value: string;
  issues: readonly ValidationIssue[];
  /** Whether the user has interacted with this sub-field (Phase 7b). */
  touched: boolean;
  onChange: (next: string) => void;
  onBlur: () => void;
  /**
   * For select sub-fields (e.g., U.S. state dropdown), the resolved
   * options. Required when `field.valueType === 'select'`; ignored
   * for text fields.
   */
  selectOptions?: readonly ISelectOption[];
}

/**
 * Optional footer toggle — Phase 7j Slice A. The composite renders a
 * checkbox below the field grid when `footerToggle` is supplied. Used
 * to host the "Mail goes to a different address" shortcut on the
 * physical-address card. Catalog booleans like
 * `<prefix>.mailing_address.same_as_current_physical` are stored
 * inverted on the engine — the parent owns the inversion. The
 * composite is a dumb checkbox: `checked` is presentation, `onChange`
 * fires the new presentation state.
 */
export interface IAddressFooterToggle {
  /** DOM id for the checkbox; also used as the htmlFor target. */
  id: string;
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Optional helper text rendered below the checkbox label. */
  helperText?: string;
}

/**
 * Optional prefill button — Phase 7j Slice B. Rendered as a header
 * strip above the field grid when `prefillButton` is supplied.
 * Hosts the "Use beneficiary's address" shortcut on the sponsor's
 * current physical address card. The composite is presentation-only;
 * the parent owns the copy semantics.
 */
export interface IAddressPrefillButton {
  label: string;
  onClick: () => void;
}

export interface AddressCompositeProps {
  /** ARIA legend for the fieldset (e.g., "Mailing address"). */
  legend: string;
  entries: readonly IAddressFieldEntry[];
  /** Optional footer checkbox; see IAddressFooterToggle. */
  footerToggle?: IAddressFooterToggle;
  /** Optional header prefill button; see IAddressPrefillButton. */
  prefillButton?: IAddressPrefillButton;
}

/**
 * 12-column proportional grid spans per address sub-field, keyed by
 * the LAST segment of the field id.
 *
 * Re-tuned 2026-05-22 per visual review — the country dropdown was
 * spanning the full row (12) which read as comically wide for a
 * single-pick control. Dropped to 6 so country pairs naturally with
 * the next visible cell (city on a 2-field place-of-birth bag,
 * street on a full address bag).
 *
 *   Row 1: country (6)              — half width, paired with whatever follows
 *   Row 2: street (12)              — full width (long string)
 *   Row 3: unit_type (3) + unit_number (3) + city (6) — apt + city
 *   Row 4: state (6) + postal (6)   — region + postal paired
 *
 * When unit_number is hidden (its visibility rule could fire), the
 * AddressComposite re-balances at render time so the row still fills
 * 12 columns — see `rebalanceSpans` below.
 */
const ADDRESS_FIELD_SPANS: Record<string, number> = {
  in_care_of_name: 12,
  street_number_and_name: 12,
  // Slice 42 (2026-05-23) — unit_type + unit_number share their own
  // row at 6/6. The "wide" branch of the city span is conditional —
  // see `spanForFieldId` below.
  unit_type: 6,
  unit_number: 6,
  city: 6,
  state: 6,
  zip_code: 6,
  country: 6,
  province: 6,
  postal_code: 6,
  // 2026-05-26 — move-in / move-out dates fold into the composite for
  // bags that carry them (address_history, most_recent_foreign_address).
  // Pair on a single row at 6/6.
  date_from: 6,
  date_to: 6,
};

const ADDRESS_DEFAULT_SPAN = 6;

/**
 * Resolve the declared span for a field id in the context of the
 * other fields in the cluster. Returns the static span from
 * ADDRESS_FIELD_SPANS or the default. The earlier `city=12 when
 * hasUnitFields` exception was removed 2026-05-26 — applicants
 * reported the city input "extending all the way to right" because
 * a full-row city read as comically wide at form widths > 600px.
 * City now stays at span 6 and pairs with state/province on the
 * next row, matching the natural address-grid rhythm.
 */
function spanForFieldId(fieldId: string, _hasUnitFields: boolean): number {
  const last = fieldId.split('.').pop() ?? '';
  return ADDRESS_FIELD_SPANS[last] ?? ADDRESS_DEFAULT_SPAN;
}

/**
 * Re-balance the per-cell spans so every visible row fills exactly
 * 12 columns. Without this, a hidden cell (e.g., `unit_number` waits
 * for `unit_type` via a visibility rule; `state` is US-only;
 * `province` is non-US-only) leaves a gap at the end of its row and
 * the layout reads as broken.
 *
 * Algorithm: scan entries in order, accumulate spans. When the next
 * entry would push past 12, close the current row and stretch its
 * cells proportionally to fill 12. Keep running through the entries
 * so subsequent rows stay tight too.
 *
 * Stable assumption: a single field's declared span never exceeds 12.
 */
function rebalanceSpans(
  entries: readonly { fieldId: string; declaredSpan: number }[],
): readonly { fieldId: string; span: number }[] {
  const rows: { fieldId: string; declaredSpan: number }[][] = [];
  let current: { fieldId: string; declaredSpan: number }[] = [];
  let used = 0;
  for (const e of entries) {
    if (used + e.declaredSpan > 12 && current.length > 0) {
      rows.push(current);
      current = [];
      used = 0;
    }
    current.push(e);
    used += e.declaredSpan;
  }
  if (current.length > 0) rows.push(current);

  const out: { fieldId: string; span: number }[] = [];
  for (const row of rows) {
    const total = row.reduce((sum, c) => sum + c.declaredSpan, 0);
    if (total === 12 || row.length === 0) {
      for (const c of row) out.push({ fieldId: c.fieldId, span: c.declaredSpan });
      continue;
    }
    // Single-cell rows keep their declared span — stretching a lone
    // half-width input to full width destroys visual rhythm. The
    // canonical offender was place_of_birth's `State` cell, which
    // ended up alone on row 2 (country + city filled row 1) and got
    // stretched to 12 columns even though "State: Tamil Nadu" reads
    // as a small text field, not a paragraph input.
    if (row.length === 1) {
      out.push({ fieldId: row[0].fieldId, span: row[0].declaredSpan });
      continue;
    }
    // Stretch proportionally. Round each cell's share; if rounding
    // leaves a remainder, drop it on the LAST cell so widget edges
    // stay on integer grid columns.
    const scale = 12 / total;
    const scaled = row.map(c => ({
      fieldId: c.fieldId,
      span: Math.round(c.declaredSpan * scale),
    }));
    const rounded = scaled.reduce((sum, c) => sum + c.span, 0);
    if (rounded !== 12 && scaled.length > 0) {
      scaled[scaled.length - 1].span += 12 - rounded;
    }
    out.push(...scaled);
  }
  return out;
}

/**
 * Display-label simplification +
 * country-aware labeling.
 *
 * Catalog labels are USCIS-aligned ("Current physical country", "Current
 * physical postal code (non-U.S.)") — bureaucratic in the form context
 * because the section/card heading already names the address bag.
 * This function projects a simplified, country-aware display label
 * per field; the catalog field stays untouched.
 *
 * Country-aware mapping:
 *   - US        → state="State", zip_code="ZIP code", city="City"
 *   - India     → province="State", postal_code="PIN code", city="City"
 *   - UK / GB   → province="County", postal_code="Postcode"
 *   - Canada    → province="Province", postal_code="Postal code"
 *   - default   → province="State / Province", postal_code="Postal code"
 */
/** Friendly label for unit_number based on the currently-selected
 *  unit_type. Falls back to a generic "Apt or unit #" when nothing's
 *  picked yet. Selected values mirror the catalog's PDF-projection
 *  vocabulary ('Apt' / 'Ste' / 'Flr') plus the friendly display
 *  versions you see in the dropdown. */
function unitNumberLabel(unitTypeValue: string | undefined): string {
  const v = (unitTypeValue ?? '').trim().toLowerCase();
  if (v === 'apt' || v.startsWith('apartment')) return 'Apartment number';
  if (v === 'ste' || v.startsWith('suite')) return 'Suite number';
  if (v === 'flr' || v.startsWith('floor')) return 'Floor number';
  return 'Apt or unit #';
}

function simplifyAddressLabel(
  fieldId: string,
  countryCode: string,
  unitTypeValue?: string,
): string | null {
  const suffix = fieldId.split('.').pop() ?? '';
  switch (suffix) {
    case 'country':
      return 'Country';
    case 'in_care_of_name':
      return 'In care of';
    case 'street_number_and_name':
      return 'Street address';
    case 'unit_type':
      return 'Apt / Suite / Unit';
    case 'unit_number':
      // Slice 42 (2026-05-23) — when the user picks a unit type
      // (Apartment / Suite / Floor), update the number label so they
      // know exactly what to type. Generic "Apt or unit #" stays as
      // the pre-selection fallback in case the visibility rule ever
      // fires before unit_type is set.
      return unitNumberLabel(unitTypeValue);
    case 'city':
      return countryCode === 'US' || countryCode === 'IN'
        ? 'City'
        : 'City / Town';
    case 'state':
      return 'State';
    case 'zip_code':
      return 'ZIP code';
    case 'province':
    case 'region':
      // Slice 42 (2026-05-23) — `region` was missing from the
      // projection switch, so place-of-birth bags
      // (birth.place.region, marriage.place.region) leaked the
      // catalog label verbatim ("Current spouse state or province
      // of birth"). Treat it identically to `province`.
      if (countryCode === 'IN') return 'State';
      if (countryCode === 'GB' || countryCode === 'UK') return 'County';
      if (countryCode === 'CA') return 'Province';
      return 'State / Province';
    case 'postal_code':
      if (countryCode === 'IN') return 'PIN code';
      if (countryCode === 'GB' || countryCode === 'UK') return 'Postcode';
      return 'Postal code';
    case 'date_from':
      return 'Date moved in';
    case 'date_to':
      return 'Date moved out';
    default:
      return null;
  }
}

function projectField(
  field: FieldDefinition,
  countryCode: string,
  unitTypeValue?: string,
): FieldDefinition {
  const simplified = simplifyAddressLabel(
    field.id as string,
    countryCode,
    unitTypeValue,
  );
  if (simplified === null || simplified === field.label) return field;
  return { ...field, label: simplified };
}

/**
 * Layout primitive for an address cluster. Renders the sub-fields in
 * a 12-column proportional grid on wide viewports and a single
 * column on narrow ones. Each entry chooses its own primitive based
 * on `field.valueType`; text fields render TextInput, select fields
 * render SelectInput. Other valueTypes silently fall through to
 * TextInput.
 *
 * The composite is a layout wrapper, not an aggregator: each
 * sub-field still emits its own onChange — the engine writes one
 * answer per sub-field.
 */
export function AddressComposite({
  legend,
  entries,
  footerToggle,
  prefillButton,
}: AddressCompositeProps): JSX.Element {
  // Country code drives label adaptation (state vs province vs County;
  // ZIP vs Postal vs PIN). Read it from whichever entry's id ends in
  // `.country` — that's the cluster's country sub-field.
  const countryEntry = entries.find(e =>
    (e.field.id as string).endsWith('.country'),
  );
  const countryCode = (countryEntry?.value ?? '').toUpperCase();
  // Pluck the unit_type value so the unit_number label can read
  // "Apartment number" / "Suite number" / "Floor number" once the
  // user picks. Empty until they select; the label falls back to a
  // generic "Apt or unit #" in that branch.
  const unitTypeEntry = entries.find(e =>
    (e.field.id as string).endsWith('.unit_type'),
  );
  const unitTypeValue = unitTypeEntry?.value;

  return (
    <fieldset className={styles.composite}>
      <legend className={styles.composite__legend}>{legend}</legend>
      {prefillButton !== undefined ? (
        <div className={styles.composite__prefill} data-address-prefill>
          <button
            type="button"
            className={styles.composite__prefill_button}
            onClick={prefillButton.onClick}
          >
            {prefillButton.label}
          </button>
        </div>
      ) : null}
      <div className={styles.composite__grid}>
        {(() => {
          // Rebalance once per render so hidden cells (visibility-gated
          // unit_number, US-vs-non-US state/province split) don't leave
          // gaps at the end of their row.
          const hasUnitFields = entries.some(e =>
            (e.field.id as string).endsWith('.unit_type'),
          );
          const declared = entries.map(e => ({
            fieldId: e.field.id as string,
            declaredSpan: spanForFieldId(
              e.field.id as string,
              hasUnitFields,
            ),
          }));
          const balanced = rebalanceSpans(declared);
          const spanByFieldId = new Map(
            balanced.map(c => [c.fieldId, c.span] as const),
          );
          return entries.map(entry => {
            const fieldId = entry.field.id as string;
            const span = spanByFieldId.get(fieldId) ?? ADDRESS_DEFAULT_SPAN;
            const cellStyle: CSSProperties = {
              gridColumn: `span ${span}`,
            };
            // Project a simplified, country-aware label per field.
            const displayField = projectField(
              entry.field,
              countryCode,
              unitTypeValue,
            );
            const child =
              entry.field.valueType === 'select' ? (
                <SelectInput
                  field={displayField}
                  options={entry.selectOptions ?? []}
                  value={entry.value}
                  issues={entry.issues}
                  touched={entry.touched}
                  onChange={entry.onChange}
                  onBlur={entry.onBlur}
                />
              ) : entry.field.valueType === 'date' ? (
                <DateInput
                  field={displayField}
                  value={entry.value}
                  issues={entry.issues}
                  touched={entry.touched}
                  onChange={entry.onChange}
                  onBlur={entry.onBlur}
                />
              ) : (
                <TextInput
                  field={displayField}
                  value={entry.value}
                  issues={entry.issues}
                  touched={entry.touched}
                  onChange={entry.onChange}
                  onBlur={entry.onBlur}
                />
              );
            return (
              <div
                key={fieldId}
                data-address-cell={fieldId}
                style={cellStyle}
              >
                {child}
              </div>
            );
          });
        })()}
      </div>
      {footerToggle !== undefined ? (
        <div className={styles.composite__footer} data-address-footer>
          <input
            id={footerToggle.id}
            type="checkbox"
            className={styles.composite__footer_checkbox}
            checked={footerToggle.checked}
            onChange={ev => footerToggle.onChange(ev.target.checked)}
          />
          <label
            htmlFor={footerToggle.id}
            className={styles.composite__footer_label}
          >
            {footerToggle.label}
          </label>
          {footerToggle.helperText !== undefined ? (
            <p className={styles.composite__footer_help}>
              {footerToggle.helperText}
            </p>
          ) : null}
        </div>
      ) : null}
    </fieldset>
  );
}
